import { motion } from "framer-motion";
import { LIFESTYLE, PETITE_LIFESTYLE } from "@/lib/watchImages";
import WatchImage from "@/components/site/WatchImage";

export const TID_LIFESTYLE_SECTION = "section-lifestyle";
export const lifestyleTid = (slug) => `lifestyle-${slug}`;

/**
 * "The CLEMENT Set" — a bright, ivory-toned editorial chapter
 * inserted between the Hero and Why-It-Exists. Sets the modern,
 * vibrant, ultra-lux country club tone for the rest of the page.
 */
export default function Lifestyle() {
  return (
    <section
      id="lifestyle"
      data-testid={TID_LIFESTYLE_SECTION}
      className="relative bg-[#F5F2EB] text-[#0C0F0A] border-y border-[rgba(31,56,42,0.15)]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 sm:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="inline-block w-12 h-px bg-[#1F382A]" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-[#1F382A] font-medium">
                The CLEMENT Set
              </span>
            </div>
            <h2 className="font-serif-display text-[#1F382A] font-light text-balance text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mt-6">
              For the people who actually{" "}
              <span className="italic text-[#C5A059]">play</span>.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-[#3a3d36] text-lg sm:text-xl leading-relaxed font-light">
              CLEMENT isn't a uniform and it isn't a logo flex. It's a
              small private watch project for people who already spend
              their weekends on the course. If you know that mile between
              the eighteenth green and the dinner reservation — this is
              for you.
            </p>
            <p className="text-[#1F382A] text-sm uppercase tracking-[0.32em] mt-8 font-medium">
              Course I · Coming Soon
            </p>
          </div>
        </div>

        {/* Editorial mosaic — clean 2x2 mosaic, all tiles equal width */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          {/* Top-left: terrace champagne (men's) */}
          <Tile
            edition={LIFESTYLE.terraceChampagne}
            className="aspect-[4/5] lg:aspect-[5/4]"
            priority
          />
          {/* Top-right: Magnolia Pearl, cream cuff (women's) */}
          <Tile
            edition={{
              slug: PETITE_LIFESTYLE[0].slug,
              src: PETITE_LIFESTYLE[0].src,
              title: PETITE_LIFESTYLE[0].title,
              caption: PETITE_LIFESTYLE[0].caption,
            }}
            className="aspect-[4/5] lg:aspect-[5/4]"
            priority
          />
          {/* Bottom-left: ladies in the cart (women's) */}
          <Tile
            edition={LIFESTYLE.ladiesCart}
            className="aspect-[4/5] lg:aspect-[5/4]"
          />
          {/* Bottom-right: marking the card (closeup wrist) */}
          <Tile
            edition={{
              slug: PETITE_LIFESTYLE[1].slug,
              src: PETITE_LIFESTYLE[1].src,
              title: PETITE_LIFESTYLE[1].title,
              caption: PETITE_LIFESTYLE[1].caption,
            }}
            className="aspect-[4/5] lg:aspect-[5/4]"
          />
        </div>

        <div className="mt-16 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-[#3a3d36] text-sm tracking-wide max-w-xl">
            These shots are how I want CLEMENT to feel in real life — quiet
            mornings, long Sundays, the people you actually share the
            course with.
          </p>
          <a
            href="#waitlist"
            className="self-start sm:self-auto inline-flex items-center gap-3 border border-[#1F382A] text-[#1F382A] px-7 py-3.5 text-[11px] uppercase tracking-[0.32em] hover:bg-[#1F382A] hover:text-[#F5F2EB] transition-colors"
          >
            Get on the list
            <span aria-hidden className="inline-block w-6 h-px bg-current" />
          </a>
        </div>
      </div>
    </section>
  );
}

function Tile({ edition: e, className = "", priority = false }) {
  return (
    <motion.figure
      data-testid={lifestyleTid(e.slug)}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, ease: "easeOut" }}
      className={`relative overflow-hidden group bg-[#1F382A]/10 ${className}`}
    >
      <WatchImage
        src={e.src}
        alt={e.title}
        fallbackTitle={e.title}
        fallbackSub="CLEMENT"
        loading={priority ? "eager" : "lazy"}
        objectFit="cover"
        testId={`${lifestyleTid(e.slug)}-image`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent opacity-90 group-hover:opacity-70 transition-opacity duration-700 pointer-events-none" />
      <figcaption className="absolute bottom-0 left-0 right-0 p-5 sm:p-7 text-[#F5F2EB]">
        <div className="text-[9px] uppercase tracking-[0.32em] text-[#C5A059] mb-2">
          {e.caption}
        </div>
        <h3 className="font-serif-display text-xl sm:text-2xl leading-tight">
          {e.title}
        </h3>
      </figcaption>
    </motion.figure>
  );
}
